import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { BadgeList } from 'components/BadgeList/BadgeList';
import { BadgeColor } from 'components/Display/Badge/Badge';
import { BasePopup, PopupWidth } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button/Button';
import { Form } from 'components/Form/Form';
import { InputLabel } from 'components/Form/Input/InputLabel/InputLabel';
import { StringListRow, StringListSection } from 'components/Form/StringListSection/StringListSection';

import { useResetOnOpen } from './useResetOnOpen';

export type EditableStringListSpec = {
  /** Unique, flat react-hook-form field key (no dots) and the key used in the onApply payload. */
  name: string;
  values: Array<string>;
  displayLabel?: string;
  badgeColor?: BadgeColor;
  displayEmptyLabel: string;
  sort?: boolean;
  limit?: number;
  editLabel?: string;
  editEmptyLabel?: string;
  addLabel: string;
  removeLabel: string;
  addAnalyticsId: string;
  removeAnalyticsId: string;
  allowRemoveLast?: boolean;
};

type EditableStringListsFormValues = Record<string, Array<StringListRow>>;

type EditableStringListsProps = {
  specs: Array<EditableStringListSpec>;
  schema: Yup.AnyObjectSchema;
  title: string;
  subtitle?: string;
  modalWidth?: PopupWidth;
  editAnalyticsId: string;
  cancelAnalyticsId: string;
  applyAnalyticsId: string;
  onApply: (values: Record<string, Array<string>>) => void;
  disabled?: boolean;
};

export const EditableStringLists = ({
  specs,
  schema,
  title,
  subtitle,
  modalWidth = 'md',
  editAnalyticsId,
  cancelAnalyticsId,
  applyAnalyticsId,
  onApply,
  disabled,
}: EditableStringListsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // useId() contains colons, which break the <button form={id}> association and let the submit
  // fall through to the enclosing page form; strip them to keep the modal form self-contained.
  const formId = `editable-string-lists-${useId().replace(/:/g, '')}`;

  const defaultValues = toDefaultValues(specs);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditableStringListsFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  useResetOnOpen(open, reset, defaultValues);

  const onSubmit = (values: EditableStringListsFormValues) => {
    onApply(Object.fromEntries(specs.map((spec) => [spec.name, (values[spec.name] ?? []).map((row) => row.value)])));
    setOpen(false);
  };

  // React synthetic events bubble through the React tree, not the DOM — so even though the modal is
  // DOM-portaled out, this submit would otherwise bubble to any enclosing page <Form> and trigger its
  // onSubmit. Stop it here so the modal stays self-contained wherever it is rendered.
  const handleFormSubmit = handleSubmit(onSubmit);

  return (
    <>
      <div className="flex flex-col items-start gap-4">
        <div className="flex flex-col gap-4">
          {specs.map((spec) => (
            <div key={spec.name} className="flex flex-col gap-1">
              <InputLabel variant="field" label={spec.displayLabel} />
              <BadgeList
                values={spec.values}
                color={spec.badgeColor}
                emptyLabel={spec.displayEmptyLabel}
                sort={spec.sort}
                limit={spec.limit}
              />
            </div>
          ))}
        </div>
        <Button
          variant="secondary"
          onClick={() => setOpen(true)}
          analyticsId={editAnalyticsId}
          LeftIcon={PencilSquareIcon}
          disabled={disabled}
        >
          {t('common:edit')}
        </Button>
      </div>

      <BasePopup open={open} setOpen={setOpen} width={modalWidth}>
        <BasePopup.Header title={title}>{subtitle}</BasePopup.Header>
        <BasePopup.Body>
          <Form
            id={formId}
            onSubmit={(event) => {
              event.stopPropagation();
              handleFormSubmit(event);
            }}
            className="space-y-6"
          >
            {specs.map((spec) => (
              <StringListSection
                key={spec.name}
                name={spec.name}
                label={spec.editLabel}
                emptyLabel={spec.editEmptyLabel}
                control={control}
                register={register}
                errors={errors}
                addLabel={spec.addLabel}
                removeLabel={spec.removeLabel}
                addAnalyticsId={spec.addAnalyticsId}
                removeAnalyticsId={spec.removeAnalyticsId}
                allowRemoveLast={spec.allowRemoveLast}
              />
            ))}
          </Form>

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={() => setOpen(false)} analyticsId={cancelAnalyticsId}>
              {t('common:cancel')}
            </Button>
            <Button type="submit" form={formId} analyticsId={applyAnalyticsId}>
              {t('common:apply')}
            </Button>
          </div>
        </BasePopup.Body>
      </BasePopup>
    </>
  );
};

const toDefaultValues = (specs: Array<EditableStringListSpec>): EditableStringListsFormValues =>
  Object.fromEntries(specs.map((spec) => [spec.name, spec.values.map((value) => ({ value }))]));
